// SPDX-License-Identifier: GPL-3.0-or-later
// The flash limiter. A full-screen flash is allowed at most 3 times in any rolling second
// (WCAG 2.3.1, three flashes or fewer in any one second). A flash that is not allowed is skipped,
// and a skipped flash does not count. When `reduceFlashing` is on, or the effects are "off",
// no flash is ever allowed.
//
// The window is strict. Flashes at 0, 400 and 800 ms are allowed. A flash at 1000 ms is not: the
// first one is still inside a closed second. A flash at 1001 ms is allowed. The limiter reads a
// clock that the caller injects, so a test needs no real time. It keeps 3 numbers and allocates
// nothing per call.

import type { FeedbackSettings } from "./settings.ts";

/** The most flashes in one window. */
export const MAX_FLASHES_PER_WINDOW = 3;

/** The window in ms. */
export const FLASH_WINDOW_MS = 1000;

/** What the limiter reads from the settings. */
export type FlashPolicy = Pick<FeedbackSettings, "reduceFlashing" | "vfxLevel">;

/** What the limiter needs. */
export interface FlashLimiterDeps {
  /** The settings now. Read at each call, so a change takes effect at once. */
  readonly policy: () => FlashPolicy;
  /** A clock in ms. It must not go back. */
  readonly now: () => number;
}

/** The limiter. */
export interface FlashLimiter {
  /** Ask for one flash. True: draw it now. False: skip it. */
  tryFlash(): boolean;
  /** Forget the flashes of the window (a new game). */
  reset(): void;
}

/** The window: the times of the last flashes, oldest first. `count` of them are in use. */
interface FlashWindow {
  readonly times: number[];
  count: number;
}

/** True when the settings forbid every flash. */
function flashLimiterDisabled(deps: FlashLimiterDeps): boolean {
  const policy = deps.policy();
  return policy.reduceFlashing || policy.vfxLevel === "off";
}

/** Forget the flashes that are more than one window old. The oldest is first. */
function flashLimiterDrop(win: FlashWindow, now: number): void {
  while (win.count > 0 && now - (win.times[0] ?? 0) > FLASH_WINDOW_MS) {
    for (let i = 1; i < win.count; i++) win.times[i - 1] = win.times[i] ?? 0;
    win.count--;
  }
}

/** The time of a flash. A clock that goes back counts as the newest flash time. */
function flashLimiterClock(win: FlashWindow, raw: number): number {
  return win.count > 0 ? Math.max(raw, win.times[win.count - 1] ?? raw) : raw;
}

/** Ask for one flash against the window. True: record it and draw it. */
function flashLimiterTry(win: FlashWindow, deps: FlashLimiterDeps): boolean {
  if (flashLimiterDisabled(deps)) return false;
  const raw = deps.now();
  if (!Number.isFinite(raw)) return false;
  // Never allow extra flashes when the clock goes back.
  const now = flashLimiterClock(win, raw);
  flashLimiterDrop(win, now);
  if (win.count >= MAX_FLASHES_PER_WINDOW) return false;
  win.times[win.count] = now;
  win.count++;
  return true;
}

/** Make the limiter. */
export function createFlashLimiter(deps: FlashLimiterDeps): FlashLimiter {
  const win: FlashWindow = {
    times: new Array<number>(MAX_FLASHES_PER_WINDOW).fill(0),
    count: 0,
  };
  return {
    tryFlash: () => flashLimiterTry(win, deps),
    reset() {
      win.count = 0;
    },
  };
}
